/**
 * v4 "Kích nét" (render_worker/sharpen.py, docs/PLAN_V4_KICH_NET_PRESET_2026-10-27.md): the report of a sharpen job in
 * plain words (no model names, no scores — the figures stay in the render machine's log and `qc.report`), Trước / Sau
 * as a close-up of the face, then "Dùng ảnh này để chỉnh tiếp" makes the sharpened photo the editor's photo (the recipe
 * is kept).
 */
import { useState, type CSSProperties } from "react";
import type { Api, Job, Me } from "../api";
import { QueueWait } from "../components/QueueWait";
import { EDIT, STATUS_LABELS, errorMessage, rejectMessage } from "../messages";
import type { EditCatalog } from "./types";

export const SHARPEN_QUEUE = "edit-sharpen";

/** `qc.report` of a sharpen job (idphoto/core/sharpen.py SharpenReport). */
export interface SharpenReport {
  width_in: number;
  height_in: number;
  width_out: number;
  height_out: number;
  sharpness_gain: number;
  skin_texture: number | null;
  /** result skin texture / sharp skin at that face size (core.sharpen.skin_vs_natural); null: no face with 106 points */
  skin_natural?: number | null;
  /** share of the skin that got made-up pore grain because the photo was too soft */
  texture_added?: number | null;
  identity: number | null;
  /** the subject's face box x, y, w, h as fractions of the picture */
  face?: number[] | null;
}

export interface Box { x: number; y: number; w: number; h: number }

export function sharpenPrice(catalog: EditCatalog | null, me: Me | null): number {
  if (me?.unmetered) return 0;
  return catalog?.pricing.sharpen ?? 10;
}

export function reportOf(job: Job): SharpenReport | null {
  const report = (job.qc as unknown as { report?: SharpenReport } | null)?.report;
  return report && typeof report.width_out === "number" ? report : null;
}

/** Below this the result's skin reads smoother than real skin (sharp skin of the smoothest people: ~0.67). */
export const SKIN_SMOOTH = 0.45;
/** From this share of the skin with made-up grain, the report says so. */
export const SKIN_ADDED = 0.25;

/** The skin line, measured against SHARP skin of that face size (not against the photo sent, which may be blurred);
 * none for a report without that measure (no face with 106 points, or a render machine without the natural-skin floor). */
export function skinLine(r: SharpenReport): string | null {
  if (r.skin_natural == null) return null;
  if (r.skin_natural < SKIN_SMOOTH) return EDIT.sharpen.skinSmooth;
  if ((r.texture_added ?? 0) >= SKIN_ADDED) return EDIT.sharpen.skinAdded;
  return EDIT.sharpen.skinKept;
}

/** The report in plain words. "Khuôn mặt giữ nguyên" only when the render machine compared the faces (it refuses
 * — and refunds — a result that changed the face). */
export function reportLines(r: SharpenReport): string[] {
  const lines = [EDIT.sharpen.sharpness(r.sharpness_gain), r.identity != null ? EDIT.sharpen.faceKept : EDIT.sharpen.faceNone];
  const skin = skinLine(r);
  if (skin) lines.push(skin);
  lines.push(EDIT.sharpen.size(r.width_in, r.height_in, r.width_out, r.height_out));
  return lines;
}

/** The face box grown to a square head close-up (1.8 × the face), kept inside the picture; fractions of the picture.
 * `aspect` = width / height of the picture. */
export function closeUp(face: number[] | null | undefined, aspect: number): Box | null {
  if (!face || face.length !== 4 || !(aspect > 0)) return null;
  const [x, y, w, h] = face;
  const side = 1.8 * Math.max(h, w * aspect);                // in fractions of the height
  const bh = Math.min(1, side);
  const bw = Math.min(1, side / aspect);
  const clamp = (v: number, hi: number) => Math.max(0, Math.min(hi, v));
  return { x: clamp(x + w / 2 - bw / 2, 1 - bw), y: clamp(y + h / 2 - bh / 2, 1 - bh), w: bw, h: bh };
}

/** CSS that shows `box` of an image of `aspect` (width / height) filling the element. */
export function boxStyle(src: string, box: Box, aspect: number): CSSProperties {
  const pos = (start: number, size: number) => (size >= 1 ? 0 : (start / (1 - size)) * 100);
  return {
    backgroundImage: `url("${src}")`,
    backgroundSize: `${100 / box.w}% ${100 / box.h}%`,
    backgroundPosition: `${pos(box.x, box.w)}% ${pos(box.y, box.h)}%`,
    aspectRatio: `${box.w * aspect} / ${box.h}`,
  };
}

function Shot({ src, box, aspect, label }: { src: string; box: Box | null; aspect: number; label: string }) {
  return (
    <figure className="sharpen-shot">
      {box ? <div className="sharpen-zoom" role="img" aria-label={label} style={boxStyle(src, box, aspect)} />
        : <img src={src} alt={label} />}
      <figcaption>{label}</figcaption>
    </figure>
  );
}

interface Props {
  api: Api;
  job: Job;
  /** the photo as it was sent (an object URL), null for a job opened from the history */
  before: string | null;
  pollError: string | null;
  using: boolean;
  /** the editor already works on this result */
  used: boolean;
  onUse: () => void;
}

export function SharpenCard({ api, job, before, pollError, using, used, onUse }: Props) {
  const [view, setView] = useState<"face" | "whole">("face");
  const report = job.status === "done" ? reportOf(job) : null;
  const after = job.files.export ?? job.files.photo;
  const aspect = report ? report.width_out / report.height_out : 1;
  const box = report ? closeUp(report.face, aspect) : null;
  const shown = view === "face" ? box : null;
  return (
    <section className="card sharpen-result" aria-labelledby="sharpen-title">
      <h2 id="sharpen-title">{EDIT.sharpen.reportTitle}</h2>
      {(job.status === "queued" || job.status === "running") && (
        <p aria-live="polite">{STATUS_LABELS[job.status]}: {job.progress?.stage ?? EDIT.sharpen.working}…</p>
      )}
      {job.status === "queued" && <QueueWait position={job.queue_position} eta={job.eta_s} />}
      {job.status === "done" && after && (
        <>
          <p className="hint">{EDIT.sharpen.reportIntro}</p>
          {box && (
            <div className="sharpen-views" role="group" aria-label={EDIT.sharpen.reportTitle}>
              <button type="button" className={`chip${view === "face" ? " is-selected" : ""}`} aria-pressed={view === "face"}
                      onClick={() => setView("face")}>{EDIT.sharpen.closeUp}</button>
              <button type="button" className={`chip${view === "whole" ? " is-selected" : ""}`} aria-pressed={view === "whole"}
                      onClick={() => setView("whole")}>{EDIT.sharpen.whole}</button>
            </div>
          )}
          <div className="sharpen-compare">
            {before && <Shot src={before} box={shown} aspect={aspect} label={EDIT.sharpen.before} />}
            <Shot src={api.fileUrl(after)} box={shown} aspect={aspect} label={EDIT.sharpen.after} />
          </div>
          {report && (
            <ul className="sharpen-report">
              {reportLines(report).map((line) => <li key={line}>{line}</li>)}
            </ul>
          )}
          <div className="result-actions">
            {used ? <p role="status" className="ok">{EDIT.sharpen.used}</p> : job.files.export && (
              <button type="button" className="btn btn-primary" onClick={onUse} disabled={using}>
                {using ? EDIT.sharpen.using : EDIT.sharpen.use}
              </button>
            )}
            {job.files.export && <a className="btn" href={api.fileUrl(job.files.export)} download>{EDIT.sharpen.download}</a>}
          </div>
        </>
      )}
      {(job.status === "rejected" || job.status === "failed" || job.status === "cancelled") && (
        <p role="alert" className="error">{rejectMessage(job.reject_reason)}</p>
      )}
      {pollError && <p className="error">{errorMessage(pollError)}</p>}
    </section>
  );
}
